import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Autocomplete,
  TextField,
  Paper,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';
import {
  MeetingRoom as MeetingIcon,
  Business as BusinessIcon,
  Handshake as HandshakeIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';

const InterlockTracker = () => {
  const [interlockAccounts, setInterlockAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch the list of unique interlock accounts
  useEffect(() => {
    const fetchInterlockAccounts = async () => {
      try {
        console.log('Fetching interlock accounts...');
        const response = await axios.get('https://sheets-dashboard-backend.gardner-johnson.workers.dev/api/meetings');
        console.log('Raw meetings response:', response.data);

        // Get unique accounts from both Interlock Account and Account Name fields
        const accountsSet = new Set();
        
        if (response.data.meetings && Array.isArray(response.data.meetings)) {
          response.data.meetings.forEach(meeting => {
            // Handle Interlock Account
            if (meeting['Interlock Account']) {
              const interlockAccount = meeting['Interlock Account'].toString().trim();
              if (interlockAccount !== '') {
                accountsSet.add(interlockAccount);
                console.log('Added Interlock Account:', interlockAccount);
              }
            }
            
            // Handle Account Name
            if (meeting['Account Name']) {
              const accountName = meeting['Account Name'].toString().trim();
              if (accountName !== '') {
                accountsSet.add(accountName);
                console.log('Added Account Name:', accountName);
              }
            }
          });
        } else {
          console.error('Invalid meetings data structure:', response.data);
        }
        
        const accounts = Array.from(accountsSet).sort((a, b) => 
          a.toLowerCase().localeCompare(b.toLowerCase())
        );
        
        console.log('Found accounts:', accounts);
        setInterlockAccounts(accounts);
      } catch (err) {
        console.error('Error fetching interlock accounts:', err);
        setError('Failed to fetch interlock accounts: ' + (err.response?.data?.error || err.message));
      }
    };
    fetchInterlockAccounts();
  }, []);

  // Fetch timeline data when an account is selected
  useEffect(() => {
    const fetchTimelineData = async () => {
      if (!selectedAccount) {
        console.log('No account selected, skipping fetch');
        return;
      }
      
      console.log('Starting timeline fetch for account:', selectedAccount);
      setLoading(true);
      setError(null);
      
      try {
        let combinedData = [];

        // Fetch meetings data
        try {
          console.log('Fetching meetings data for account:', selectedAccount);
          const meetingsResponse = await axios.get('https://sheets-dashboard-backend.gardner-johnson.workers.dev/api/meetings');
          console.log('Raw meetings response:', meetingsResponse.data);
          
          if (meetingsResponse.data?.meetings) {
            // Deduplicate meetings by Meeting ID and filter by account
            const meetingsMap = new Map();
            let matchCount = 0;
            
            meetingsResponse.data.meetings.forEach(meeting => {
              const interlockMatch = meeting['Interlock Account']?.toLowerCase() === selectedAccount.toLowerCase();
              const accountMatch = meeting['Account Name']?.toLowerCase() === selectedAccount.toLowerCase();
              
              if ((interlockMatch || accountMatch) && meeting['Meeting ID']) {
                matchCount++;
                console.log('Found matching meeting:', {
                  id: meeting['Meeting ID'],
                  date: meeting['Meeting Date'],
                  interlockAccount: meeting['Interlock Account'],
                  accountName: meeting['Account Name']
                });
                
                // Keep the most recent meeting if duplicate IDs exist
                const existingMeeting = meetingsMap.get(meeting['Meeting ID']);
                if (!existingMeeting || new Date(meeting['Meeting Date']) > new Date(existingMeeting['Meeting Date'])) {
                  meetingsMap.set(meeting['Meeting ID'], meeting);
                }
              }
            });
            
            console.log(`Found ${matchCount} meetings matching account ${selectedAccount}`);
            const meetings = Array.from(meetingsMap.values());
            console.log('After deduplication:', meetings.length, 'meetings');

            // Add meetings to combined data
            const mappedMeetings = meetings.map(meeting => ({
              type: 'meeting',
              date: meeting['Meeting Date'] ? new Date(meeting['Meeting Date']) : new Date(),
              data: {
                ...meeting,
                'Meeting Type': meeting['Meeting Type'] || 'N/A',
                'Attendees': meeting['External Attendees'] || 'N/A',
                'Title': meeting['Meeting Title'] || 'N/A'
              }
            }));
            
            console.log('Mapped meetings:', mappedMeetings);
            combinedData.push(...mappedMeetings);
          } else {
            console.warn('No meetings data in response:', meetingsResponse.data);
          }
        } catch (err) {
          console.warn('Error fetching meetings:', err.message);
          // Continue with other data sources
        }

        // Fetch opportunities data
        try {
          console.log('Fetching opportunities data for account:', selectedAccount);
          // Fetch all opportunities and filter client-side
          const opportunitiesResponse = await axios.get('https://sheets-dashboard-backend.gardner-johnson.workers.dev/api/opportunities');
          console.log('Raw opportunities response:', {
            fullResponse: opportunitiesResponse,
            data: opportunitiesResponse.data,
            hasDataProp: opportunitiesResponse.data?.data ? 'yes' : 'no',
            structure: Object.keys(opportunitiesResponse.data || {})
          });
          
          if (opportunitiesResponse.data?.opportunities) {
            // Log a sample of account names for debugging
            console.log('Sample of account names in opportunities:', 
              opportunitiesResponse.data.opportunities
                .slice(0, 10)
                .map(opp => ({
                  account_name: opp.account_name,
                  partner_account: opp.partner_account
                })));

            // Log the account we're searching for
            console.log('Searching for account:', {
              searchTerm: selectedAccount,
              searchTermLower: selectedAccount.toLowerCase()
            });

            const opportunities = opportunitiesResponse.data.opportunities.filter(opp => {
              const accountMatch = opp.account_name && opp.account_name.toLowerCase().includes(selectedAccount.toLowerCase());
              const partnerMatch = opp.partner_account && opp.partner_account.toLowerCase().includes(selectedAccount.toLowerCase());
              return accountMatch || partnerMatch;
            });

            console.log('Received opportunities total:', opportunitiesResponse.data.opportunities.length);
            console.log('Filtered opportunities for', selectedAccount + ':', opportunities.length);

            // Add opportunities to combined data
            combinedData.push(...opportunities.map(opp => {
              console.log('Processing opportunity:', opp);
              return {
                type: 'opportunity',
                date: opp.created_date ? new Date(opp.created_date) : new Date(),
                data: {
                  'Title': opp.opportunity_name || 'Unnamed Opportunity',
                  'Account Name': opp.account_name || 'N/A',
                  'Partner Account': opp.partner_account || 'N/A',
                  'Amount': opp.amount ? `$${Number(opp.amount).toLocaleString()}` : 'N/A',
                  'Stage': opp.stage || 'N/A',
                  'Probability': opp.probability ? `${opp.probability}%` : 'N/A',
                  'Created Date': opp.created_date ? new Date(opp.created_date).toLocaleDateString() : 'N/A',
                  'Close Date': opp.close_date ? new Date(opp.close_date).toLocaleDateString() : 'N/A',
                  'Type': opp.type || 'N/A',
                  'Lead Source': opp.lead_source || 'N/A'
                }
              };
            }));
          } else {
            console.warn('No opportunities data in response:', opportunitiesResponse.data);
          }
        } catch (err) {
          console.warn('Error fetching opportunities:', err.message, err);
          // Continue with deal registrations
        }

        // Fetch partner deal registrations
        try {
          console.log('Fetching deal registrations data for account:', selectedAccount);
          const dealRegsResponse = await axios.get('https://sheets-dashboard-backend.gardner-johnson.workers.dev/api/dealreg');
          console.log('Deal registrations response:', dealRegsResponse.data);
          
          if (dealRegsResponse.data?.dealRegistrations) {
            const dealRegs = dealRegsResponse.data.dealRegistrations.filter(
              reg => 
                (reg['Company Account Name'] && reg['Company Account Name'].toLowerCase().includes(selectedAccount.toLowerCase()))
            );
            console.log('Filtered deal registrations:', dealRegs);

            // Add deal registrations to combined data
            combinedData.push(...dealRegs.map(reg => ({
              type: 'dealreg',
              date: reg['Created Date'] ? new Date(reg['Created Date']) : new Date(),
              data: {
                ...reg,
                'Partner Name': reg['Partner Name'],
                'Status': reg['Status'],
                'Contact': reg['Contact Name'],
                'Title': reg['Title'],
                'Email': reg['Email']
              }
            })));
          }
        } catch (err) {
          console.warn('Error fetching deal registrations:', err.message);
          // Continue with what we have
        }

        // Sort combined data by date
        combinedData.sort((a, b) => b.date - a.date);

        console.log('Final timeline data:', {
          totalItems: combinedData.length,
          byType: {
            meetings: combinedData.filter(item => item.type === 'meeting').length,
            opportunities: combinedData.filter(item => item.type === 'opportunity').length,
            dealRegs: combinedData.filter(item => item.type === 'dealreg').length
          },
          items: combinedData.map(item => ({
            type: item.type,
            date: item.date,
            title: item.data['Title'] || item.data['Meeting Title'] || 'N/A'
          }))
        });

        // Only set error if we have no data at all
        if (combinedData.length === 0) {
          console.warn('No data found for account:', selectedAccount);
          setError('No data found for the selected account');
        } else {
          setError(null);
        }

        setTimelineData(combinedData);
        setLoading(false);
      } catch (err) {
        console.error('Timeline fetch error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          stack: err.stack
        });
        setError(`Failed to fetch timeline data: ${err.message}`);
        setLoading(false);
      }
    };

    fetchTimelineData();
  }, [selectedAccount]);

  const getTimelineIcon = (type) => {
    switch (type) {
      case 'meeting':
        return <MeetingIcon />;
      case 'opportunity':
        return <BusinessIcon />;
      case 'dealreg':
        return <HandshakeIcon />;
      default:
        return null;
    }
  };

  const renderTimelineContent = (item) => {
    switch (item.type) {
      case 'meeting':
        return (
          <>
            <Typography variant="subtitle1" component="div">
              {item.data['Meeting Type']} Meeting
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Date: {new Date(item.data['Meeting Date']).toLocaleDateString()}
            </Typography>
            <Typography variant="body2">
              Attendees: {item.data['Attendees']}
            </Typography>
            <Typography variant="body2">
              {item.data['Title']}
            </Typography>
          </>
        );
      
      case 'opportunity':
        console.log('Rendering opportunity:', item);
        return (
          <>
            <Typography variant="subtitle1" component="div" style={{ fontWeight: 'bold' }}>
              {item.data['Title']}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Partner: {item.data['Partner Account']}
            </Typography>
            <Typography variant="body2">
              Amount: {item.data['Amount']} | Stage: {item.data['Stage']}
            </Typography>
            <Typography variant="body2">
              Created: {item.data['Created Date']} | Close Date: {item.data['Close Date']}
            </Typography>
            {item.data['Probability'] !== 'N/A' && (
              <Typography variant="body2">
                Probability: {item.data['Probability']}
              </Typography>
            )}
            <Typography variant="body2" color="textSecondary">
              Type: {item.data['Type']} | Source: {item.data['Lead Source']}
            </Typography>
          </>
        );
      
      case 'dealreg':
        return (
          <>
            <Typography variant="subtitle1" component="div">
              Deal Registration
            </Typography>
            <Typography variant="body2">
              Partner: {item.data['Partner Name']}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Contact: {item.data['Contact']} ({item.data['Title']})
            </Typography>
            <Typography variant="body2">
              Status: {item.data['Status']}
            </Typography>
          </>
        );
      
      default:
        return (
          <Typography variant="body1">
            Unknown event type
          </Typography>
        );
    }
  };

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Interlock Account Tracker
        </Typography>
        
        <Autocomplete
          value={selectedAccount}
          onChange={(event, newValue) => setSelectedAccount(newValue)}
          options={interlockAccounts}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Interlock Account"
              variant="outlined"
              fullWidth
            />
          )}
          sx={{ mb: 4 }}
        />

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Timeline position="alternate">
            {timelineData.map((item, index) => (
              <TimelineItem key={index}>
                <TimelineSeparator>
                  <TimelineDot color="primary">
                    {getTimelineIcon(item.type)}
                  </TimelineDot>
                  {index < timelineData.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Card>
                    <CardContent>
                      {renderTimelineContent(item)}
                    </CardContent>
                  </Card>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </Box>
    </Container>
  );
};

export default InterlockTracker;
